import Hls from 'hls.js'
import { getAuth, getRadioManifestUrl, getRadioNowPlaying } from '../../services/api'

const NOW_PLAYING_POLL_MS = 10000
const CONNECT_TIMEOUT_MS = 20000
const LIVE_DELAY_MS = 12000

let audio = null
let hls = null
let pollTimer = null
let connectTimeout = null
let listeners = []
let levelDetails = null
let dateRangeActive = false

let volume = 1
let state = { status: 'idle', track: null, clockOffsetMs: 0 } // idle | connecting | playing | error
let eqUrl = ''
let eq = null

function emit() {
  for (const cb of listeners) cb(state)
}

function setState(patch) {
  state = { ...state, ...patch }
  syncEq()
  emit()
}

function syncEq() {
  const url = (state.track && state.track.eq) || ''
  if (url === eqUrl) return
  eqUrl = url
  eq = null
  if (!url) return

  const headers = url.indexOf('/api/radio/') !== -1 ? { Authorization: 'Bearer ' + getAuth() } : {}
  window.fetch(url, { headers })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (url !== eqUrl || !data || typeof data.levels !== 'string') return
      eq = data
    })
    .catch(() => {})
}

async function pollNowPlaying() {
  try {
    const { current, serverTime } = await getRadioNowPlaying()
    const clockOffsetMs = typeof serverTime === 'number' ? serverTime - Date.now() : state.clockOffsetMs
    if (dateRangeActive) {
      // The in-stream title wins; only borrow the eq link if the stream lacks it.
      const t = state.track
      if (t && !t.eq && current && current.eq && current.title === t.title) {
        setState({ clockOffsetMs, track: { ...t, eq: current.eq } })
      } else {
        setState({ clockOffsetMs })
      }
    } else {
      setState({ track: current || null, clockOffsetMs })
    }
  } catch (err) {
  }
}

function readDateRange(frag) {
  if (!frag || !levelDetails || !levelDetails.dateRanges) return
  const at = frag.programDateTime
  if (!at) return

  for (const id in levelDetails.dateRanges) {
    if (!Object.prototype.hasOwnProperty.call(levelDetails.dateRanges, id)) continue
    const r = levelDetails.dateRanges[id]
    const startMs = r.startDate && r.startDate.getTime()
    if (startMs == null || at < startMs) continue
    if (r.duration && at >= startMs + r.duration * 1000) continue

    const a = r.attr || {}
    dateRangeActive = true
    setState({
      track: {
        title: a['X-TITLE'] || '',
        artist: a['X-ARTIST'] || '',
        albumImage: a['X-ALBUM-IMAGE'] || '',
        eq: a['X-EQ'] || (state.track && state.track.title === a['X-TITLE'] ? state.track.eq : '') || '',
        startedAt: startMs,
        endsAt: r.duration ? startMs + r.duration * 1000 : null,
      },
    })
    return
  }
}


function serverNow() {
  return Date.now() + state.clockOffsetMs
}

function playbackNow() {
  if (hls && hls.playingDate) return hls.playingDate.getTime()
  if (audio && audio.getStartDate) {
    const start = audio.getStartDate().getTime()
    if (!isNaN(start)) return start + audio.currentTime * 1000
  }
  return serverNow() - LIVE_DELAY_MS
}


function getLevel() {
  const track = state.track
  if (state.status !== 'playing' || !eq || !track || !track.startedAt) return null
  const { fps, bands, cells, frameCount, levels } = eq
  const frame = Math.floor(((playbackNow() - track.startedAt) / 1000) * fps)
  if (frame < 0 || frame >= frameCount) return 0
  let sum = 0
  for (let b = 0; b < bands; b++) {
    sum += parseInt(levels[frame * bands + b], cells + 1) || 0
  }
  return sum / (bands * cells)
}

function getBitrate() {
  if (!hls || !hls.levels || !hls.levels.length) return null
  const level = hls.levels[Math.max(0, hls.currentLevel)]
  return level && level.bitrate ? Math.round(level.bitrate / 1000) : null
}

function startPolling() {
  stopPolling()
  pollNowPlaying()
  pollTimer = setInterval(pollNowPlaying, NOW_PLAYING_POLL_MS)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function onStateChange(cb) {
  listeners.push(cb)
  return () => {
    listeners = listeners.filter((it) => it !== cb)
  }
}

function isPlaying() {
  return state.status === 'connecting' || state.status === 'playing'
}

function start() {
  if (isPlaying()) return

  setState({ status: 'connecting', track: null })

  audio = new window.Audio()
  audio.preload = 'none'
  audio.volume = volume

  const manifestUrl = getRadioManifestUrl()


  let settled = false
  const onPlaying = () => {
    if (settled) return
    settled = true
    clearTimeout(connectTimeout)
    setState({ status: 'playing' })
    startPolling()
  }
  const onFailure = () => {
    if (settled) return
    settled = true
    clearTimeout(connectTimeout)
    setState({ status: 'error' })
    stop()
  }

  audio.addEventListener('playing', onPlaying)
  audio.addEventListener('error', onFailure)
  connectTimeout = setTimeout(onFailure, CONNECT_TIMEOUT_MS)

  if (Hls.isSupported()) {
    hls = new Hls({
      lowLatencyMode: false,
      backBufferLength: 30,
      xhrSetup: (xhr, url) => {
        if (url.indexOf('/api/radio/') !== -1) {
          xhr.setRequestHeader('Authorization', 'Bearer ' + getAuth())
        }
      },
    })
    hls.on(Hls.Events.ERROR, (event, data) => {
      if (data.fatal) onFailure()
    })
    hls.on(Hls.Events.LEVEL_UPDATED, (event, data) => {
      levelDetails = data && data.details
    })
    hls.on(Hls.Events.FRAG_CHANGED, (event, data) => {
      readDateRange(data && data.frag)
    })
    hls.loadSource(manifestUrl)
    hls.attachMedia(audio)
  } else if (audio.canPlayType('application/vnd.apple.mpegurl')) {
    audio.src = `${manifestUrl}?token=${encodeURIComponent(getAuth())}`
  } else {
    onFailure()
    return Promise.reject(new Error('HLS not supported'))
  }

  return audio.play().catch((err) => {
    onFailure()
    throw err
  })
}

function stop() {
  clearTimeout(connectTimeout)
  stopPolling()
  if (audio) {
    audio.pause()
    audio.removeAttribute('src')
    audio.load()
    audio = null
  }
  if (hls) {
    hls.destroy()
    hls = null
  }
  levelDetails = null
  dateRangeActive = false
  setState({ status: 'idle', track: null })
}

function setVolume(v) {
  volume = v
  if (audio) audio.volume = v
}

export default {
  start,
  stop,
  isPlaying,
  onStateChange,
  getState: () => state,
  serverNow,
  getLevel,
  getBitrate,
  setVolume,
}
